import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { CtaLink } from "@/components/common/Buttons";
import { MaskedLines, PixelCluster } from "@/components/common/Kinetic";
import { HeroVisual } from "@/components/home/HeroVisual";
import { scrollToId } from "@/lib/lenis";

const VALUES = [
  { t: "Custom-Built Solutions", d: "Designed around your workflows and goals." },
  { t: "Scalable Engineering", d: "Architecture ready for more users and data." },
  { t: "Business-Focused Delivery", d: "Every feature tied to a practical outcome." },
];

const fade = (d) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] } });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const visualY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-[#030712] text-white" data-testid="hero-section">
      <motion.div style={{ y: gridY }} className="grid-dark pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_60%_30%,black_20%,transparent_80%)]" />
      <div className="pointer-events-none absolute right-[-10%] top-[-20%] h-[640px] w-[640px] rounded-full bg-blue-600/20 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-[-30%] left-[-10%] h-[520px] w-[520px] rounded-full bg-violet-600/15 blur-[140px]" />

      <div className="container-q relative grid items-center gap-16 pb-20 pt-32 sm:pt-40 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-44">
        <div className="lg:col-span-7">
          <motion.p {...fade(0)} className="eyebrow flex items-center gap-3 text-cyan-300">
            <PixelCluster /> Software development & technology solutions
          </motion.p>
          <MaskedLines
            testId="hero-headline"
            className="mt-7 text-[2.55rem] font-extrabold leading-[1.02] tracking-[-0.045em] sm:text-6xl lg:text-[4.1rem] xl:text-[4.6rem]"
            lines={["Build Smarter", "Digital Products.", <span key="g" className="grad-text">Scale With Confidence.</span>]}
          />
          <motion.p {...fade(0.55)} className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            Quantivo helps businesses transform ideas into reliable software through custom development, SaaS engineering, business automation, and scalable digital solutions.
          </motion.p>
          <motion.div {...fade(0.7)} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <CtaLink to="/contact" data-testid="hero-discuss-btn">Discuss Your Project</CtaLink>
            <CtaLink href="#services" variant="ghostDark" arrow="right" data-testid="hero-services-btn"
              onClick={(e) => { e.preventDefault(); scrollToId("services"); }}>
              Explore Our Services
            </CtaLink>
          </motion.div>
        </div>

        <motion.div style={{ y: visualY }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.35, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5">
          <HeroVisual />
        </motion.div>
      </div>

      <div className="relative border-t border-white/10">
        <ul className="container-q grid divide-y divide-white/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {VALUES.map((v, i) => (
            <motion.li key={v.t} {...fade(0.85 + i * 0.1)} className="flex items-start gap-4 py-6 sm:px-6 sm:first:pl-0" data-testid={`value-prop-${i}`}>
              <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-cyan-400/40 text-cyan-300"><Check className="h-3.5 w-3.5" /></span>
              <div>
                <p className="text-[15px] font-bold text-white">{v.t}</p>
                <p className="mt-1 text-[14px] text-slate-400">{v.d}</p>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
};
